import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { BarChart3, TrendingUp, DollarSign, ShoppingCart, Receipt, Send, RefreshCw, Clock3, FileDown, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : window.location.origin);

export default function Reports() {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [fiscalFilter, setFiscalFilter] = useState('all');
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [fiscalEvents, setFiscalEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [sendingSii, setSendingSii] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [syncingSii, setSyncingSii] = useState(false);
  const [downloadingXml, setDownloadingXml] = useState(false);
  const [openingPdf, setOpeningPdf] = useState(false);
  const [xmlPreview, setXmlPreview] = useState('');
  const [loadingXmlPreview, setLoadingXmlPreview] = useState(false);

  useEffect(() => {
    fetchSales();
  }, []);

  const fetchSales = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sales?limit=100`, { withCredentials: true });
      setSales(data);
    } catch (error) {
      console.error('Error fetching sales:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('es-CL', {
      dateStyle: 'short',
      timeStyle: 'short'
    });
  };

  const getFiscalBadge = (status) => {
    const map = {
      pending_folio: 'bg-amber-50 text-amber-700',
      ready_to_send: 'bg-blue-50 text-blue-700',
      sent: 'bg-indigo-50 text-indigo-700',
      accepted: 'bg-emerald-50 text-emerald-700',
      rejected: 'bg-red-50 text-red-700',
      not_required: 'bg-stone-100 text-stone-600'
    };
    return map[status] || 'bg-stone-100 text-stone-600';
  };

  const loadFiscalEvents = async (saleId) => {
    setLoadingEvents(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/sales/${saleId}/fiscal-events`, { withCredentials: true });
      setFiscalEvents(data);
    } catch (error) {
      setFiscalEvents([]);
      toast.error(error.response?.data?.detail || 'No se pudo cargar historial fiscal');
    } finally {
      setLoadingEvents(false);
    }
  };

  const loadXmlPreview = async (saleId) => {
    setLoadingXmlPreview(true);
    try {
      const response = await axios.get(`${API_URL}/api/sales/${saleId}/fiscal-document/xml`, {
        withCredentials: true,
        responseType: 'text'
      });
      setXmlPreview(typeof response.data === 'string' ? response.data : '');
    } catch (error) {
      setXmlPreview('');
    } finally {
      setLoadingXmlPreview(false);
    }
  };

  const openSaleDetail = async (sale) => {
    setSelectedSale(sale);
    setDetailOpen(true);
    await Promise.all([loadFiscalEvents(sale.id), loadXmlPreview(sale.id)]);
  };

  const sendToSii = async () => {
    if (!selectedSale) return;
    setSendingSii(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/sales/${selectedSale.id}/sii/send`, {}, { withCredentials: true });
      setSelectedSale(data.sale);
      toast.success('Documento enviado al proveedor SII');
      await Promise.all([fetchSales(), loadFiscalEvents(selectedSale.id)]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en envío SII');
      await Promise.all([fetchSales(), loadFiscalEvents(selectedSale.id)]);
    } finally {
      setSendingSii(false);
    }
  };

  const checkSiiStatus = async () => {
    if (!selectedSale) return;
    setCheckingStatus(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/sales/${selectedSale.id}/sii/status`, { withCredentials: true });
      setSelectedSale(prev => ({ ...prev, ...data }));
      toast.success('Estado fiscal actualizado');
      await Promise.all([fetchSales(), loadFiscalEvents(selectedSale.id)]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo consultar estado fiscal');
    } finally {
      setCheckingStatus(false);
    }
  };

  const syncPendingSii = async () => {
    setSyncingSii(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/sii/sync-pending`, {}, { withCredentials: true });
      toast.success(`Sincronización SII: revisadas ${data.checked}, actualizadas ${data.updated}`);
      await fetchSales();
      if (selectedSale) {
        await Promise.all([checkSiiStatus(), loadFiscalEvents(selectedSale.id)]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al sincronizar estados SII');
    } finally {
      setSyncingSii(false);
    }
  };

  const downloadXml = async () => {
    if (!selectedSale) return;
    setDownloadingXml(true);
    try {
      const response = await axios.get(`${API_URL}/api/sales/${selectedSale.id}/fiscal-document/xml`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `venta_${selectedSale.sale_number || selectedSale.id}.xml`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('XML descargado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo descargar XML');
    } finally {
      setDownloadingXml(false);
    }
  };

  const openPdf = async () => {
    if (!selectedSale) return;
    setOpeningPdf(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/sales/${selectedSale.id}/fiscal-document/pdf`, { withCredentials: true });
      if (data?.pdf_url) {
        window.open(data.pdf_url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('PDF no disponible para este documento');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo abrir PDF');
    } finally {
      setOpeningPdf(false);
    }
  };

  const filterByPeriod = (salesData) => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    
    switch (period) {
      case 'today':
        return salesData.filter(s => s.created_at.startsWith(today));
      case 'week':
        const weekAgo = new Date(now.setDate(now.getDate() - 7));
        return salesData.filter(s => new Date(s.created_at) >= weekAgo);
      case 'month':
        const monthAgo = new Date(now.setMonth(now.getMonth() - 1));
        return salesData.filter(s => new Date(s.created_at) >= monthAgo);
      default:
        return salesData;
    }
  };

  const filterByFiscalStatus = (salesData) => {
    if (fiscalFilter === 'all') return salesData;
    return salesData.filter(s => (s.fiscal_status || 'not_required') === fiscalFilter);
  };

  const filteredSales = filterByFiscalStatus(filterByPeriod(sales));
  const totalSales = filteredSales.reduce((sum, s) => sum + s.total, 0);
  const avgTicket = filteredSales.length > 0 ? totalSales / filteredSales.length : 0;

  // Payment method breakdown
  const paymentBreakdown = filteredSales.reduce((acc, sale) => {
    acc[sale.payment_method] = (acc[sale.payment_method] || 0) + sale.total;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-stone-900 tracking-tight font-heading">Reportes</h1>
          <p className="text-stone-500 mt-1">Análisis de ventas y métricas</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-44 h-12 rounded-xl" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Hoy</SelectItem>
              <SelectItem value="week">Última semana</SelectItem>
              <SelectItem value="month">Último mes</SelectItem>
              <SelectItem value="all">Todo</SelectItem>
            </SelectContent>
          </Select>
          <Select value={fiscalFilter} onValueChange={setFiscalFilter}>
            <SelectTrigger className="w-52 h-12 rounded-xl" data-testid="fiscal-filter-select">
              <SelectValue placeholder="Estado fiscal" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="pending_folio">Pendiente folio</SelectItem>
              <SelectItem value="ready_to_send">Listo para envío</SelectItem>
              <SelectItem value="sent">Enviado</SelectItem>
              <SelectItem value="accepted">Aceptado</SelectItem>
              <SelectItem value="rejected">Rechazado</SelectItem>
              <SelectItem value="not_required">No aplica</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={syncPendingSii}
            disabled={syncingSii}
            className="h-12 rounded-xl"
            data-testid="sync-sii-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {syncingSii ? 'Sincronizando...' : 'Sync SII'}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-white border-stone-200 rounded-2xl" data-testid="total-sales-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-stone-500 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-500" />
              Total Ventas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-stone-900">{formatCurrency(totalSales)}</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-stone-200 rounded-2xl" data-testid="transactions-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-stone-500 flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-blue-500" />
              Transacciones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-stone-900">{filteredSales.length}</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-stone-200 rounded-2xl" data-testid="avg-ticket-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-stone-500 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-amber-500" />
              Ticket Promedio
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-stone-900">{formatCurrency(avgTicket)}</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-stone-200 rounded-2xl" data-testid="payment-breakdown-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-stone-500 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-purple-500" />
              Por Medio de Pago
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {Object.entries(paymentBreakdown).map(([method, amount]) => (
              <div key={method} className="flex justify-between text-sm">
                <span className="text-stone-600 capitalize">{method}</span>
                <span className="font-medium">{formatCurrency(amount)}</span>
              </div>
            ))}
            {Object.keys(paymentBreakdown).length === 0 && (
              <p className="text-sm text-stone-500">Sin ventas</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sales Table */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <div className="p-4 border-b border-stone-200">
          <h2 className="text-lg font-bold text-stone-900">Detalle de Ventas</h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="font-semibold"># Venta</TableHead>
              <TableHead className="font-semibold">Fecha</TableHead>
              <TableHead className="font-semibold">Cajero</TableHead>
              <TableHead className="font-semibold">Documento</TableHead>
              <TableHead className="font-semibold">Folio</TableHead>
              <TableHead className="font-semibold">Estado Fiscal</TableHead>
              <TableHead className="font-semibold">Track ID</TableHead>
              <TableHead className="font-semibold">Items</TableHead>
              <TableHead className="font-semibold">Pago</TableHead>
              <TableHead className="font-semibold text-right">Total</TableHead>
              <TableHead className="font-semibold text-right">Acción</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredSales.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-12 text-stone-500">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                  No hay ventas en este período
                </TableCell>
              </TableRow>
            ) : (
              filteredSales.map((sale) => (
                <TableRow key={sale.id} className="hover:bg-stone-50/50" data-testid={`sale-row-${sale.id}`}>
                  <TableCell className="font-medium text-stone-900">#{sale.sale_number}</TableCell>
                  <TableCell className="text-stone-600">{formatDateTime(sale.created_at)}</TableCell>
                  <TableCell className="text-stone-600">{sale.cashier_name}</TableCell>
                  <TableCell className="text-stone-600 capitalize">{sale.document_type || 'ticket'}</TableCell>
                  <TableCell className="text-stone-600">{sale.folio_number || '-'}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getFiscalBadge(sale.fiscal_status)}`}>
                      {sale.fiscal_status || 'not_required'}
                    </span>
                  </TableCell>
                  <TableCell className="text-stone-600 text-xs max-w-[140px] truncate" title={sale.sii_track_id || '-'}>
                    {sale.sii_track_id || '-'}
                  </TableCell>
                  <TableCell className="text-stone-600">
                    {sale.items?.length || 0} productos
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      sale.payment_method === 'efectivo' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'
                    }`}>
                      {sale.payment_method}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-bold text-stone-900">{formatCurrency(sale.total)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openSaleDetail(sale)}
                      data-testid={`sale-detail-${sale.id}`}
                      className="rounded-lg"
                    >
                      Ver detalle
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="sale-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-blue-600" />
              Venta #{selectedSale?.sale_number} - Fiscal
            </DialogTitle>
          </DialogHeader>

          {selectedSale && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-stone-50 rounded-xl p-3">
                  <p className="text-xs text-stone-500">Documento</p>
                  <p className="font-medium capitalize">{selectedSale.document_type || 'ticket'}</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-3">
                  <p className="text-xs text-stone-500">Folio</p>
                  <p className="font-medium">{selectedSale.folio_number || '-'}</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-3">
                  <p className="text-xs text-stone-500">Track ID</p>
                  <p className="font-medium text-sm break-all">{selectedSale.sii_track_id || '-'}</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-3">
                  <p className="text-xs text-stone-500">Estado</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getFiscalBadge(selectedSale.fiscal_status)}`}>
                    {selectedSale.fiscal_status || 'not_required'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Button
                  onClick={sendToSii}
                  disabled={sendingSii || !['boleta', 'factura'].includes(selectedSale.document_type)}
                  className="h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="send-sii-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sendingSii ? 'Enviando...' : 'Enviar a SII'}
                </Button>
                <Button
                  variant="outline"
                  onClick={checkSiiStatus}
                  disabled={checkingStatus}
                  className="h-11 rounded-xl"
                  data-testid="check-sii-status-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {checkingStatus ? 'Consultar...' : 'Consultar Estado'}
                </Button>
                <Button
                  variant="outline"
                  onClick={downloadXml}
                  disabled={downloadingXml}
                  className="h-11 rounded-xl"
                  data-testid="download-xml-btn"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  {downloadingXml ? 'Descargando...' : 'Descargar XML'}
                </Button>
                <Button
                  variant="outline"
                  onClick={openPdf}
                  disabled={openingPdf}
                  className="h-11 rounded-xl"
                  data-testid="open-pdf-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {openingPdf ? 'Abriendo...' : 'Ver PDF'}
                </Button>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="border border-stone-200 rounded-xl p-4">
                  <p className="font-semibold text-stone-900 mb-3 flex items-center gap-2">
                    <Clock3 className="w-4 h-4 text-stone-600" />
                    Historial Fiscal
                  </p>
                  {loadingEvents ? (
                    <p className="text-sm text-stone-500">Cargando historial...</p>
                  ) : fiscalEvents.length === 0 ? (
                    <p className="text-sm text-stone-500">Sin eventos fiscales registrados.</p>
                  ) : (
                    <div className="space-y-3 max-h-72 overflow-y-auto">
                      {fiscalEvents.map((event) => (
                        <div key={event.id} className="p-3 rounded-lg bg-stone-50 border border-stone-200">
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-sm font-medium text-stone-900">{event.message}</p>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getFiscalBadge(event.status)}`}>
                              {event.status}
                            </span>
                          </div>
                          <p className="text-xs text-stone-500 mt-1">
                            {formatDateTime(event.created_at)} - {event.user_name || 'system'}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="border border-stone-200 rounded-xl p-4">
                  <p className="font-semibold text-stone-900 mb-3 flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-blue-600" />
                    Vista previa XML
                  </p>
                  {loadingXmlPreview ? (
                    <p className="text-sm text-stone-500">Cargando XML...</p>
                  ) : xmlPreview ? (
                    <pre className="text-xs bg-stone-50 border border-stone-200 rounded-lg p-3 overflow-auto max-h-72 whitespace-pre-wrap break-all">{xmlPreview}</pre>
                  ) : (
                    <p className="text-sm text-stone-500">XML no disponible todavía.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
